import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";

export default function TermsOfUse() {
  const [businessName, setBusinessName] = useState("Nityavastra");

  useEffect(() => {
    let active = true;
    api.get("/config/business-identity")
      .then(({ data }) => {
        if (active && data?.public_business_name) {
          setBusinessName(data.public_business_name);
        }
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const sections = [
    {
      title: "Using this website",
      content: <p>You may browse products and use this website for lawful personal shopping. Please provide accurate account, delivery, and contact details when placing an order. Keep your sign-in details secure and contact us if you suspect unauthorized account access.</p>,
    },
    {
      title: "Products and prices",
      content: <p>Product descriptions, availability, prices, taxes, delivery charges, and eligible offers are shown on the website and at checkout. Product colours may appear slightly different across screens. If an item or listing needs correction, we may contact you about the order and arrange an appropriate cancellation or refund where payment has already been collected.</p>,
    },
    {
      title: "Orders and payment",
      content: <p>An order is submitted when you complete checkout. Available payment methods, including any eligible cash on delivery option, are shown at checkout. Online payments are handled by the payment provider shown there. We send order updates using the contact details you provide.</p>,
    },
    {
      title: "Shipping, returns, and refunds",
      content: <p>Delivery availability, shipping charges, and estimates depend on the destination and order. Read our <Link to="/pages/shipping-policy" className="text-[#7C1F30] underline">Shipping Policy</Link> for details. Return eligibility, exclusions, and refund handling are set out in our <Link to="/pages/return-policy" className="text-[#7C1F30] underline">Return &amp; Exchange Policy</Link> and <Link to="/cancellation-refund-policy" className="text-[#7C1F30] underline">Cancellation &amp; Refund Policy</Link>. Your applicable consumer rights remain available.</p>,
    },
    {
      title: "Partner products",
      content: <p>Some products may be supplied by partner sellers. The product page and order information provide the relevant product details. {businessName} manages this website, checkout, and customer support for orders placed here.</p>,
    },
    {
      title: "Website content",
      content: <p>The text, images, branding, and design on this website are intended for viewing and shopping. Please do not copy or reuse them for commercial purposes without permission from the relevant rights holder.</p>,
    },
    {
      title: "Privacy and cookies",
      content: <p>Read our <Link to="/privacy-policy" className="text-[#7C1F30] underline">Privacy Policy</Link> and <Link to="/cookie-policy" className="text-[#7C1F30] underline">Cookie Policy</Link> to understand how information and optional analytics are handled.</p>,
    },
    {
      title: "Questions and complaints",
      content: <p>For order help, use our <Link to="/contact" className="text-[#7C1F30] underline">Contact page</Link>. For a formal complaint or escalation, see <Link to="/grievance-redressal" className="text-[#7C1F30] underline">Grievance Redressal</Link>, which lists the Grievance Officer and complaint process.</p>,
    },
    {
      title: "Updates to these terms",
      content: <p>We may update these terms as the website and our services change. The current version will be posted here with its effective date. Changes do not remove rights that apply to orders already placed under law.</p>,
    },
  ];

  return (
    <main className="min-h-[70vh] bg-[#FFF9F0] px-4 pb-16 pt-[132px] sm:px-8">
      <SEO title="Terms of Use" description="Terms for shopping on the Nityavastra website." />
      <div className="mx-auto max-w-4xl">
        <header className="rounded-3xl bg-[#7C1F30] p-7 text-[#FFF9F0] sm:p-10">
          <p className="text-xs uppercase tracking-[0.25em] text-[#E7C78A]">Shopping information</p>
          <h1 className="mt-3 font-serif text-4xl sm:text-5xl">Terms of Use</h1>
          <p className="mt-4 max-w-2xl text-sm leading-7 text-white/85">
            These terms explain how to use this website and place orders with {businessName}.
            The name shown here is the public name used on this website.
          </p>
        </header>
        <div className="mt-6 space-y-4">
          {sections.map((section) => (
            <section key={section.title} className="rounded-2xl border border-[#E5D5BF] bg-white p-6 text-sm leading-7 text-[#5D4B3E] shadow-sm sm:p-8">
              <h2 className="font-serif text-2xl text-[#2A1508]">{section.title}</h2>
              <div className="mt-3">{section.content}</div>
            </section>
          ))}
        </div>
      </div>
    </main>
  );
}
